/**
 * Everpret CartStore
 * Thin wrapper around Shopify's native Cart AJAX API. Shopify's cart is always
 * the single source of truth — this module never computes totals/prices itself,
 * it only reads back whatever Shopify returns.
 *
 * Reuses the theme's existing document-level cart event bus (cart:change,
 * cart:refresh, cart:prepare-bundled-sections) so native PDP add-to-cart and
 * this store never fork cart state, and so the existing <cart-drawer> element's
 * Section Rendering re-render keeps working unmodified.
 */
(function (window) {
  'use strict';

  var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';

  function url(path) {
    return root.replace(/\/$/, '') + path;
  }

  function collectBundledSections() {
    var detail = { sections: [] };
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: detail })
    );
    return detail.sections;
  }

  /** Other apps/scripts on this store poll /cart.js heavily; a burst of our
   *  own requests can land on top of theirs and get 429'd by Shopify's
   *  storefront rate limiter. 429s are transient — retry a couple of times
   *  with backoff before giving up, rather than leaving state stale. */
  function fetchWithRetry(input, init, attempt) {
    attempt = attempt || 0;
    return fetch(input, init).then(function (response) {
      if (response.status === 429 && attempt < 3) {
        var delay = 300 * Math.pow(2, attempt);
        return new Promise(function (resolve) {
          setTimeout(resolve, delay);
        }).then(function () {
          return fetchWithRetry(input, init, attempt + 1);
        });
      }
      return response;
    });
  }

  function request(path, body) {
    var sections = collectBundledSections();
    var payload = Object.assign({}, body);
    if (sections.length) payload.sections = sections.join(',');

    return fetchWithRetry(url(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) {
          var error = new Error((json && json.description) || 'Cart request failed');
          error.response = json;
          throw error;
        }
        return json;
      });
    });
  }

  function CartStore() {
    this.cart = null;
    this.loading = false;
    this.updating = false;
    this.rewardSyncing = false;
    this.error = null;
    this._listeners = [];
    this._bindNativeEvents();
  }

  CartStore.prototype._bindNativeEvents = function () {
    var self = this;
    document.addEventListener('cart:change', function (event) {
      if (event.detail && event.detail.cart) {
        self._setCart(event.detail.cart);
      }
    });
    document.addEventListener('cart:refresh', function () {
      self.fetchCart();
    });
  };

  CartStore.prototype.subscribe = function (fn) {
    this._listeners.push(fn);
    return function unsubscribe() {
      this._listeners = this._listeners.filter(function (l) {
        return l !== fn;
      });
    }.bind(this);
  };

  CartStore.prototype._notify = function () {
    this._listeners.forEach(function (fn) {
      fn(this.cart);
    }, this);
  };

  CartStore.prototype._setCart = function (cart) {
    this.cart = cart;
    this._notify();
  };

  CartStore.prototype._broadcast = function (baseEvent, cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: baseEvent, cart: cart } })
    );
  };

  CartStore.prototype.fetchCart = function () {
    var self = this;
    this.loading = true;
    return fetchWithRetry(url('/cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        self.loading = false;
        self._setCart(cart);
        return cart;
      })
      .catch(function (err) {
        self.loading = false;
        self.error = err;
        throw err;
      });
  };

  /** items: [{ id, quantity, properties }] */
  CartStore.prototype.addItems = function (items, opts) {
    var self = this;
    opts = opts || {};
    this.updating = true;
    this.error = null;
    return request('/cart/add.js', { items: items })
      .then(function (addResult) {
        self.updating = false;
        // /cart/add.js returns only the added line items, not the full cart —
        // refetch for authoritative totals, but carry forward the bundled
        // section HTML already returned by the add call so the existing
        // <cart-drawer> can still patch itself in place without a 3rd request.
        return self.fetchCart().then(function (fullCart) {
          if (addResult && addResult.sections) fullCart.sections = addResult.sections;
          self._broadcast(opts.baseEvent || 'cart-store:add', fullCart);
          return fullCart;
        });
      })
      .catch(function (err) {
        self.updating = false;
        self.error = err;
        throw err;
      });
  };

  /** updates: { [lineKey]: quantity } — quantity 0 removes the line */
  CartStore.prototype.updateItems = function (updates, opts) {
    var self = this;
    opts = opts || {};
    this.updating = true;
    this.error = null;
    return request('/cart/update.js', { updates: updates })
      .then(function (cart) {
        self.updating = false;
        self._broadcast(opts.baseEvent || 'cart-store:update', cart);
        return cart;
      })
      .catch(function (err) {
        self.updating = false;
        self.error = err;
        throw err;
      });
  };

  CartStore.prototype.updateItem = function (key, quantity, opts) {
    var updates = {};
    updates[key] = quantity;
    return this.updateItems(updates, opts);
  };

  CartStore.prototype.removeItem = function (key, opts) {
    return this.updateItem(key, 0, opts);
  };

  /** Change a single line's variant while preserving quantity/properties:
   *  add the new variant, then zero the old line — never a UI-only swap. */
  CartStore.prototype.changeVariant = function (fromKey, toVariantId, quantity, properties, opts) {
    var self = this;
    opts = opts || {};
    this.updating = true;
    return request('/cart/add.js', { items: [{ id: toVariantId, quantity: quantity, properties: properties || {} }] })
      .then(function () {
        var updates = {};
        updates[fromKey] = 0;
        return request('/cart/update.js', { updates: updates });
      })
      .then(function (cart) {
        self.updating = false;
        self._broadcast(opts.baseEvent || 'cart-store:variant-change', cart);
        return cart;
      })
      .catch(function (err) {
        self.updating = false;
        self.error = err;
        throw err;
      });
  };

  CartStore.prototype.clearCart = function (opts) {
    var self = this;
    opts = opts || {};
    return request('/cart/clear.js', {}).then(function (cart) {
      self._broadcast(opts.baseEvent || 'cart-store:clear', cart);
      return cart;
    });
  };

  CartStore.prototype.applyDiscount = function (codes, opts) {
    opts = opts || {};
    var discount = Array.isArray(codes) ? codes.join(',') : codes;
    return request('/cart/update.js', { discount: discount }).then(
      function (cart) {
        this._broadcast(opts.baseEvent || 'cart-store:discount', cart);
        return cart;
      }.bind(this)
    );
  };

  CartStore.prototype.updateNote = function (note, opts) {
    opts = opts || {};
    return request('/cart/update.js', { note: note }).then(
      function (cart) {
        this._broadcast(opts.baseEvent || 'cart-store:note', cart);
        return cart;
      }.bind(this)
    );
  };

  CartStore.prototype.refresh = function () {
    return this.fetchCart();
  };

  window.Everpret = window.Everpret || {};
  window.Everpret.cartStore = window.Everpret.cartStore || new CartStore();
})(window);
